import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddImageModal, EditImageModal, DeleteImageModal } from './PortfolioModals';

interface PortfolioImage {
  id: number;
  professional_id: number;
  image_url?: string;
  thumbnail_url?: string;
  title: string;
  description?: string;
  category: string;
  subcategory?: string;
  is_featured: boolean;
  is_before_after: boolean;
  before_image_url?: string;
  after_image_url?: string;
  tags?: string;
  created_at: string;
  updated_at: string;
}

interface Professional {
  id: number;
  name: string;
  type: 'salud' | 'belleza';
  specialty: string;
}

interface PortfolioStats {
  total_images: number;
  featured_count: number;
  before_after_count: number;
  unique_categories: number;
  first_image_date: string;
  last_image_date: string;
}

interface ThemeConfig {
  gradient: string;
  bgGradient: string;
  primary: string;
  title: string;
  addButtonText: string;
  icons: {
    main: string;
    add: string;
    featured: string;
    beforeAfter: string;
    edit: string;
    delete: string;
    upload: string;
  };
}

const UniversalPortfolio: React.FC = () => {
  const navigate = useNavigate();
  
  // Estados principales
  const [user, setUser] = useState<any>(null);
  const [professional, setProfessional] = useState<Professional | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioImage[]>([]);
  const [stats, setStats] = useState<PortfolioStats | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  
  // Estados de UI
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<PortfolioImage | null>(null);
  const [selectedImage, setSelectedImage] = useState<PortfolioImage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{show: boolean, progress: number, message: string}>({
    show: false, progress: 0, message: ''
  });

  // Configuración del tema basada en el tipo de profesional
  const getThemeConfig = (professionalType: 'salud' | 'belleza'): ThemeConfig => {
    if (professionalType === 'belleza') {
      return {
        gradient: 'from-pink-500 to-purple-500',
        bgGradient: 'from-pink-50 to-purple-50',
        primary: 'pink',
        title: 'Mi Portfolio de Belleza',
        addButtonText: 'Agregar Trabajo',
        icons: {
          main: '💄',
          add: '➕',
          featured: '⭐',
          beforeAfter: '🔄',
          edit: '✏️',
          delete: '🗑️',
          upload: '📸'
        }
      };
    } else {
      return {
        gradient: 'from-blue-500 to-green-500',
        bgGradient: 'from-blue-50 to-green-50',
        primary: 'blue',
        title: 'Mi Portfolio Médico',
        addButtonText: 'Agregar Caso',
        icons: {
          main: '🏥',
          add: '➕',
          featured: '⭐',
          beforeAfter: '📊',
          edit: '✏️',
          delete: '🗑️',
          upload: '📁'
        }
      };
    }
  };

  const [theme, setTheme] = useState<ThemeConfig>(getThemeConfig('belleza'));

  useEffect(() => {
    // Verificar usuario y cargar datos iniciales
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setTheme(getThemeConfig(parsedUser.professional_type || 'belleza'));
      
      if (parsedUser.id) {
        loadPortfolioData(parsedUser.id);
        loadAvailableCategories(parsedUser.professional_type || 'belleza');
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const loadPortfolioData = async (professionalId: number) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'todos') params.append('category', selectedCategory);
      if (showFeaturedOnly) params.append('featured_only', 'true');
      
      const API_URL = process.env.REACT_APP_API_URL || 'https://consultar-api.onrender.com';
      const url = `${API_URL}/api/portfolio/professional/${professionalId}?${params}`;
      console.log('Loading portfolio from:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Error al cargar el portfolio');
      }
      
      const data = await response.json();
      
      // ✅ CAMBIO REALIZADO: Procesar data.images en lugar de data.portfolio
      if (data.success) {
        setPortfolio(data.images || []); // ← data.images, no data.portfolio
        
        // Crear professional desde user
        if (user) {
          setProfessional({
            id: user.id,
            name: `${user.nombre} ${user.apellido}`,
            type: user.professional_type || 'belleza',
            specialty: user.especialidad || ''
          });
        }
        
        // Calcular stats desde images
        const images = data.images || [];
        setStats({
          total_images: images.length,
          featured_count: images.filter((img: any) => img.is_featured).length,
          before_after_count: images.filter((img: any) => img.is_before_after).length,
          unique_categories: new Set(images.map((img: any) => img.category)).size,
          first_image_date: images.length > 0 ? images[images.length - 1].created_at : '',
          last_image_date: images.length > 0 ? images[0].created_at : ''
        });
        
        // Categorías únicas - ✅ CÓDIGO ACTUALIZADO
        // Categorías como strings simples
        const uniqueCategories = Array.from(new Set(images.map((img: any) => img.category).filter(Boolean)));
        setCategories(uniqueCategories as any[]);
      } else {
        throw new Error(data.message || 'Error en la respuesta del servidor');
      }
      
    } catch (err: any) {
      console.error('Error loading portfolio:', err);
      setError('No se pudo cargar el portfolio completamente. Mostrando datos disponibles.');
      // Cargar datos mock para desarrollo
      loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCategories = async (professionalType: string) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://consultar-api.onrender.com';
      const response = await fetch(`${API_URL}/api/portfolio/categories/${professionalType}`);
      const data = await response.json();
      
      // ✅ CÓDIGO ACTUALIZADO
      if (data.success && data.categories) {
        // Convertir cualquier formato a strings
        const categoryValues = data.categories.map((cat: any) => {
          if (typeof cat === 'string') return cat;
          if (typeof cat === 'object' && cat !== null) {
            return cat.label || cat.value || String(cat);
          }
          return String(cat);
        });
        setAvailableCategories(categoryValues);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      // Fallback a categorías por defecto
      const defaultCategories = professionalType === 'belleza' 
        ? ['Pestañas y Cejas', 'Manicura y Pedicura', 'Tratamientos Faciales', 'Antes y Después']
        : ['Instalaciones', 'Casos Clínicos', 'Procedimientos', 'Antes y Después'];
      setAvailableCategories(defaultCategories);
    }
  };

  const loadMockData = () => {
    const mockProfessional: Professional = {
      id: user?.id || 1,
      name: `${user?.nombre || 'Usuario'} ${user?.apellido || 'Demo'}`,
      type: user?.professional_type || 'belleza',
      specialty: user?.especialidad || 'General'
    };

    const mockStats: PortfolioStats = {
      total_images: 3,
      featured_count: 2,
      before_after_count: 1,
      unique_categories: 2,
      first_image_date: '2025-01-15',
      last_image_date: '2025-08-29'
    };

    const mockPortfolio: PortfolioImage[] = [];
    const mockCategories: any[] = [];

    setProfessional(mockProfessional);
    setPortfolio(mockPortfolio);
    setStats(mockStats);
    setCategories(mockCategories);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    if (user?.id) {
      loadPortfolioData(user.id);
    }
  };

  const handleFeaturedToggle = () => {
    setShowFeaturedOnly(!showFeaturedOnly);
    if (user?.id) {
      loadPortfolioData(user.id);
    }
  };

  const handleAddImage = () => {
    setShowAddModal(true);
  };

  const handleImagesAdded = (newImages: PortfolioImage[]) => {
    setPortfolio(prev => [...newImages, ...prev]);
    setStats(prev => prev ? { 
      ...prev, 
      total_images: prev.total_images + newImages.length,
      featured_count: prev.featured_count + newImages.filter(img => img.is_featured).length
    } : null);
    
    // Recargar datos para actualizar categorías
    if (user?.id) {
      setTimeout(() => loadPortfolioData(user.id), 1000);
    }
  };

  const handleEditImage = (image: PortfolioImage) => {
    setSelectedImage(image);
    setShowEditModal(true);
  };

  const handleImageUpdated = (updatedImage: PortfolioImage) => {
    setPortfolio(prev => prev.map(img => img.id === updatedImage.id ? updatedImage : img));
    setShowEditModal(false);
    setSelectedImage(null);
  };

  const handleDeleteClick = (image: PortfolioImage) => {
    setImageToDelete(image);
    setShowDeleteModal(true);
  };

  const handleDeleteImage = (imageId: number) => {
    // Actualizar el portfolio eliminando la imagen
    setPortfolio(prev => prev.filter(img => img.id !== imageId));
    setStats(prev => prev ? { ...prev, total_images: prev.total_images - 1 } : null);
    setShowDeleteModal(false);
    setImageToDelete(null);
  };

  const handleToggleFeatured = async (image: PortfolioImage) => {
    if (!user?.id) return;
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://consultar-api.onrender.com';
      // El endpoint pasa a pedir token y a verificar que el portfolio sea el
      // tuyo: sin este header, destacar una imagen deja de funcionar.
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/portfolio/professional/${user.id}/image/${image.id}/featured`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ is_featured: !image.is_featured })
      });
      
      if (response.ok) {
        setPortfolio(prev => prev.map(img => 
          img.id === image.id ? { ...img, is_featured: !img.is_featured } : img
        ));
        setStats(prev => prev ? {
          ...prev,
          featured_count: prev.featured_count + (image.is_featured ? -1 : 1)
        } : null);
      } else {
        throw new Error('Error al actualizar imagen destacada');
      }
    } catch (err) {
      console.error('Error toggling featured:', err);
      setError('Error al actualizar imagen destacada');
    }
  };

  const getImageUrl = (image: PortfolioImage) => {
    const baseUrl = 'http://localhost:5000';
    
    if (image.is_before_after) {
      return {
        before: image.before_image_url?.startsWith('http') 
          ? image.before_image_url 
          : `${baseUrl}${image.before_image_url}`,
        after: image.after_image_url?.startsWith('http') 
          ? image.after_image_url 
          : `${baseUrl}${image.after_image_url}`
      };
    } else {
      return image.image_url?.startsWith('http') 
        ? image.image_url 
        : `${baseUrl}${image.image_url}`;
    }
  };

  const filteredPortfolio = portfolio.filter(image => {
    if (selectedCategory !== 'todos' && image.category !== selectedCategory) return false;
    if (showFeaturedOnly && !image.is_featured) return false;
    return true;
  });

  if (!user) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${theme.bgGradient} flex items-center justify-center`}>
        <div className="text-center">
          <p className="text-red-600">Error: Usuario no encontrado</p>
          <button 
            onClick={() => navigate('/login')}
            className={`mt-4 bg-gradient-to-r ${theme.gradient} text-white px-4 py-2 rounded-lg hover:shadow-lg transition-shadow`}
          >
            Ir a Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bgGradient}`}>
      {/* Progress bar para uploads */}
      {uploadProgress.show && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white px-4 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <span className="text-sm font-medium">{uploadProgress.message}</span>
            <div className="w-32 bg-blue-800 rounded-full h-2">
              <div 
                className="bg-white h-2 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress.progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{theme.icons.main} {theme.title}</h1>
              <p className="text-gray-600">
                Muestra tus mejores trabajos y casos
                {stats && <span className="ml-2">• {stats.total_images} imágenes</span>}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* ✅ NUEVO: Mostrar contador de fotos y deshabilitar cuando llegue al límite */}
              <div className="flex flex-col items-end">
                <button
                  onClick={handleAddImage}
                  disabled={portfolio.length >= 5}
                  className={`px-6 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                    portfolio.length >= 5
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : `bg-gradient-to-r ${theme.gradient} text-white hover:shadow-lg`
                  }`}
                  title={portfolio.length >= 5 ? 'Límite de fotos alcanzado' : ''}
                >
                  <span>{theme.icons.upload}</span>
                  <span>{theme.addButtonText}</span>
                </button>
                <span className="text-xs text-gray-500 mt-1">
                  {portfolio.length}/5 fotos
                </span>
              </div>
              <button 
                onClick={() => navigate(-1)} 
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ← Volver
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Estadísticas */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">{stats.total_images}</p>
                <p className="text-sm text-gray-600">Total Imágenes</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="text-center">
                <p className="text-3xl font-bold text-yellow-600">{stats.featured_count}</p>
                <p className="text-sm text-gray-600">Destacadas</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{stats.before_after_count}</p>
                <p className="text-sm text-gray-600">Antes/Después</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{stats.unique_categories}</p>
                <p className="text-sm text-gray-600">Categorías</p>
              </div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleCategoryChange('todos')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === 'todos'
                    ? `bg-${theme.primary}-500 text-white shadow-sm`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas ({portfolio.length})
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? `bg-${theme.primary}-500 text-white shadow-sm`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat} ({portfolio.filter(img => img.category === cat).length})
                </button>
              ))}
            </div>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showFeaturedOnly}
                  onChange={handleFeaturedToggle}
                  className="rounded text-blue-600"
                />
                <span>Solo destacadas</span>
              </label>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
            <div className="flex items-start">
              <span className="text-yellow-600 text-lg mr-3">⚠️</span>
              <div>
                <h4 className="font-medium text-yellow-800 mb-1">Aviso</h4>
                <p className="text-sm text-yellow-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Grid de Imágenes */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="h-64 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredPortfolio.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPortfolio.map(image => {
              const imageUrl = getImageUrl(image);
              return (
                <div key={image.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-200">
                  <div className="relative group">
                    {image.is_before_after && typeof imageUrl === 'object' ? (
                      <div className="grid grid-cols-2 h-64">
                        <img
                          src={imageUrl.before}
                          alt={`${image.title} - Antes`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x300?text=Imagen+no+disponible';
                          }}
                        />
                        <img
                          src={imageUrl.after}
                          alt={`${image.title} - Después`}
                          className="w-full h-full object-cover border-l-2 border-white"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x300?text=Imagen+no+disponible';
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-white rounded-full p-2 shadow-md">
                            {theme.icons.beforeAfter}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={typeof imageUrl === 'string' ? imageUrl : 'https://via.placeholder.com/300x300?text=Imagen+no+disponible'}
                        alt={image.title}
                        className="w-full h-64 object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x300?text=Imagen+no+disponible';
                        }}
                      />
                    )}
                    
                    {image.is_featured && (
                      <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-medium shadow-sm">
                        {theme.icons.featured} Destacado
                      </div>
                    )}
                    
                    {/* Acciones */}
                    <div className="absolute bottom-3 right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleToggleFeatured(image)}
                        className={`p-2 rounded-full text-white text-sm shadow-lg ${
                          image.is_featured ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-500 hover:bg-gray-600'
                        } transition-colors`}
                        title={image.is_featured ? 'Quitar de destacados' : 'Marcar como destacado'}
                      >
                        {theme.icons.featured}
                      </button>
                      <button
                        onClick={() => handleEditImage(image)}
                        className="p-2 bg-blue-500 text-white rounded-full text-sm hover:bg-blue-600 shadow-lg transition-colors"
                        title="Editar"
                      >
                        {theme.icons.edit}
                      </button>
                      <button
                        onClick={() => handleDeleteClick(image)}
                        className="p-2 bg-red-500 text-white rounded-full text-sm hover:bg-red-600 shadow-lg transition-colors"
                        title="Eliminar"
                      >
                        {theme.icons.delete}
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 truncate">{image.title}</h3>
                    {image.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{image.description}</p>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 bg-${theme.primary}-100 text-${theme.primary}-800 text-xs rounded-full`}>
                        {image.category}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(image.created_at).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                    {image.tags && (
                      <div className="flex flex-wrap gap-1">
                        {image.tags.split(',').slice(0, 3).map((tag, index) => (
                          <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">{theme.icons.main}</span>
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-3">
              {selectedCategory === 'todos' 
                ? 'Aún no tienes imágenes en tu portfolio' 
                : `No hay imágenes en la categoría "${selectedCategory}"`
              }
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              {selectedCategory === 'todos'
                ? `Comienza subiendo tus mejores ${user.professional_type === 'belleza' ? 'trabajos de belleza' : 'casos clínicos'}. Las imágenes se procesarán y optimizarán automáticamente.`
                : 'Intenta con otra categoría o agrega una nueva imagen a esta categoría.'
              }
            </p>
            {/* ✅ MODIFICADO: Deshabilitar cuando llegue al límite */}
            {portfolio.length < 5 ? (
              <button
                onClick={handleAddImage}
                className={`bg-gradient-to-r ${theme.gradient} text-white px-8 py-3 rounded-lg hover:shadow-lg transition-shadow flex items-center space-x-2 mx-auto`}
              >
                <span>{theme.icons.upload}</span>
                <span>{theme.addButtonText}</span>
              </button>
            ) : (
              <div className="text-center">
                <button
                  disabled
                  className="bg-gray-300 text-gray-500 px-8 py-3 rounded-lg cursor-not-allowed flex items-center space-x-2 mx-auto"
                >
                  <span>{theme.icons.upload}</span>
                  <span>Límite alcanzado (5/5)</span>
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  Eliminá alguna foto para subir una nueva
                </p>
              </div>
            )}
          </div>
        )}

        {/* Información adicional */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start">
            <span className="text-blue-600 text-lg mr-3">💡</span>
            <div>
              <h4 className="font-medium text-blue-800 mb-2">Sistema de Portfolio Avanzado</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p>• <strong>Carga directa de archivos:</strong> Sube hasta 5 imágenes simultáneamente, máximo 10MB cada una</p>
                <p>• <strong>Procesamiento automático:</strong> Las imágenes se optimizan automáticamente para web</p>
                <p>• <strong>Antes/Después:</strong> Carga comparativas para mostrar resultados</p>
                <p>• <strong>Organización inteligente:</strong> Categorías, etiquetas y sistema de destacados</p>
                <p>• <strong>Portfolio profesional:</strong> Mejora tu presencia online y atrae más {user.professional_type === 'belleza' ? 'clientes' : 'pacientes'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modales */}
      <AddImageModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onImageAdded={handleImagesAdded}
        professionalId={user?.id || 0}
        availableCategories={availableCategories}
        professionalType={user?.professional_type || 'belleza'}
        currentPhotoCount={portfolio.length} // ✅ NUEVO: Pasar cantidad actual de fotos
      />

      <EditImageModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedImage(null);
        }}
        onImageUpdated={handleImageUpdated}
        image={selectedImage}
        professionalId={user?.id || 0}
        availableCategories={availableCategories}
        professionalType={user?.professional_type || 'belleza'}
      />

      <DeleteImageModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setImageToDelete(null);
        }}
        onImageDeleted={handleDeleteImage}
        image={imageToDelete}
        professionalId={user?.id || 0}
      />
    </div>
  );
};

export default UniversalPortfolio;